import { getEnv } from '@/helpers/environment'

import { errorTracker } from '@/services/errorTracking'

import { appendUniqueItems, prependUniqueItems } from './arrayHelpers'
import { type IdentityFormData, type IdentityFormField, builtInFields, customFields } from './identityHelpers'

function csvToArray(csv: string): string[] {
  return csv.split(',').map((s) => s.trim())
}

export interface GetIdentityFormFieldsOptions {
  omit?: string[]
  readOnlyFields?: string[]
}

// TODO Handle different payment methods
export function getIdentityFormFields(
  answers: IdentityFormData,
  configs: string[] = [],
  options?: GetIdentityFormFieldsOptions,
): IdentityFormField[] {
  const result: IdentityFormField[] = []
  const fields = getAvailableFields(answers)
  const names = getFieldNames(configs)
  const { readOnlyFields } = options ?? {}
  const fieldNames = combineAndFilterIdentityFields(names, [], options?.omit)

  for (const name of fieldNames) {
    const field = fields[name]
    if (field) {
      result.push({
        ...field,
        readOnly: readOnlyFields?.includes(name) ?? false,
      })
    } else {
      errorTracker.track(new Error(`Custom identity field '${name}' is not defined.`), {
        operation: 'identityForm.getFields',
        fieldName: name,
      })
    }
  }

  return result
}

export function combineAndFilterIdentityFields(names: string[], required: string[], omit?: string[]): string[] {
  const omitted = new Set(omit)
  const fields = mergeRequiredFields(names, required)
  return fields.filter((name) => !omitted.has(name))
}

function mergeRequiredFields(configured: string[], required: string[]): string[] {
  const remainder = new Set(required)
  const result: string[] = []
  for (let i = 0; i < configured.length; i++) {
    const name = configured[i]
    if (remainder.has(name)) {
      if (remainder.size === required.length) {
        result.push(...required)
      }
      remainder.delete(name)
    } else {
      result.push(name)
    }
  }

  if (remainder.size === 0) {
    return configured
  }
  if (remainder.size === required.length) {
    return configured.concat(required)
  }
  return result
}

function getAvailableFields(answers: IdentityFormData): Record<string, IdentityFormField> {
  return { ...customFields(answers), ...builtInFields() }
}

function getFieldNames(configs: string[]): string[] {
  const configured = getConfiguredFieldNames(configs)
  const alwaysRequired = csvToArray('first_name, last_name, email')
  return Array.from(new Set(prependUniqueItems(configured, alwaysRequired)))
}

function getConfiguredFieldNames(configurations: string[]): string[] {
  const env = getEnv()
  const base = (env.config.web?.identity_form ?? env.config.config?.identity_form ?? []) as string[]

  const extrasConfigs = configurations.map(csvToArray).sort((a, b) => a.length - b.length)
  const longest = extrasConfigs.pop()

  if (!longest) {
    return base
  }

  const result = prependUniqueItems(longest, base)
  return extrasConfigs.reduce(appendUniqueItems, result)
}

export function getConfiguredIdentityFieldNames(configurations: string[] = []): string[] {
  return getFieldNames(configurations)
}
